import DialogModal from 'src/components/dialog_modal/dialog_modal.vue'
import NameWithEmoji from 'src/components/name_with_emoji/name_with_emoji.vue'
import Popover from 'src/components/popover/popover.vue'
import UnicodeDomainIndicator from 'src/components/unicode_domain_indicator/unicode_domain_indicator.vue'
import UserAvatar from 'src/components/user_avatar/user_avatar.vue'

import { useUsersStore } from 'src/stores/users.js'

import {
  displayName,
  profileLink,
} from 'src/services/user_display/user_display.js'

const UserListPopover = {
  name: 'UserListPopover',
  inheritAttrs: false,
  props: {
    userIds: { type: Set, default: () => new Set() },
    load: Function,
    needsLoad: Boolean,
    fullOnly: Boolean,
    loadMore: Function,
    hasMore: Boolean,
  },
  components: {
    DialogModal,
    NameWithEmoji,
    UnicodeDomainIndicator,
    Popover,
    UserAvatar,
  },
  emits: ['close'],
  data: () => ({
    loadingList: false,
    loadingMore: false,
    loaded: false,
    failed: false,
    failedMore: false,
    full: false,
    page: 0,
    returnFocus: null,
  }),
  mounted() {
    if (this.fullOnly) this.openFull()
  },
  watch: {
    'users.length'(count) {
      this.page = Math.min(this.page, Math.max(0, Math.ceil(count / 20) - 1))
    },
  },
  computed: {
    loading() {
      return this.loadingList || this.loadingMore
    },
    users() {
      return [...this.userIds]
        .map((id) => useUsersStore().findUser(id))
        .filter(Boolean)
    },
    usersCapped() {
      return this.full
        ? this.users.slice(this.page * 20, (this.page + 1) * 20)
        : this.users.slice(0, 16)
    },
  },
  methods: {
    onKeydown(event) {
      if (this.full && event.key === 'Escape') {
        event.stopPropagation()
        this.closeFull()
      } else if (this.full && event.key === 'Tab') {
        const items = [
          ...this.$refs.view.$el.querySelectorAll(
            'button:not(:disabled), a[href], select',
          ),
        ]
        const first = items[0]
        const last = items.at(-1)
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault()
          last?.focus()
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault()
          first?.focus()
        }
      }
    },
    async openFull() {
      if (this.fullOnly) this.returnFocus = document.activeElement
      this.page = 0
      this.full = true
      this.loadUsers(true)
      await this.$nextTick()
      this.$refs.view?.$el.querySelector('.participants-close')?.focus()
    },
    async closeFull() {
      this.full = false
      if (this.fullOnly) this.$emit('close')
      await this.$nextTick()
      if (this.fullOnly) this.returnFocus?.focus()
      else {
        const trigger = this.$refs.view?.$refs?.trigger
        const target =
          trigger?.querySelector('a, button, [tabindex]') || trigger
        if (target?.matches('span')) target.tabIndex = -1
        target?.focus()
      }
    },
    async moreUsers() {
      if (this.loading) return
      this.loadingMore = true
      this.failed = false
      try {
        await this.loadMore()
      } catch {
        this.failed = true
        this.failedMore = true
      } finally {
        this.loadingMore = false
      }
    },
    async loadUsers(retry = false) {
      if (
        !this.load ||
        this.loadingList ||
        (!retry && this.loadingMore) ||
        (!retry && (this.failed || this.loaded))
      )
        return
      if (!retry && !this.needsLoad && this.users.length === this.userIds.size)
        return
      this.loadingList = true
      this.failed = false
      this.failedMore = false
      try {
        // False means discarded (a reset or click race), so a later hover retries.
        // An applied request can still return fewer people than the post count.
        this.loaded = (await this.load()) !== false
        this.failed = false
      } catch {
        this.failed = true
        this.failedMore = false
      } finally {
        this.loadingList = false
      }
    },
    nameOf: displayName,
    generateProfileLink: profileLink,
  },
}

export default UserListPopover
